import { Injectable } from '@angular/core';
import { INeighborhood, Neighborhood } from '../models/neighborhood.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { ReplaySubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NeighborhoodService {

  public loading: boolean = false;
  // Indica si GET /neighborhoods/:id esta en vuelo
  public loadingCurrent: boolean = false;
  public neighborhoods: Neighborhood[] = [];

  public currentNeighborhood?: Neighborhood;
  public subCurrentNeighborhood: Subject<Neighborhood> = new Subject<Neighborhood>();

  // Emite la lista completa de colonias cada vez que termina getAll().
  //
  // Es un ReplaySubject(1) y NO un Subject normal a proposito: getAll() se
  // dispara desde el constructor de este servicio, y al ser providedIn: 'root'
  // el servicio se construye antes que cualquier pagina que quiera escucharlo.
  // Con un Subject normal la emision ocurriria cuando todavia no hay ningun
  // suscriptor, y el suscriptor tardio (la pagina) nunca se enteraria de que la
  // lista ya cargo. El ReplaySubject(1) guarda el ultimo valor y se lo entrega a
  // quien se suscriba despues. Es exactamente la misma razon por la que
  // AuthService.subCurrentUser tambien es un ReplaySubject(1).
  //
  // IMPORTANTE para quien se suscriba: esto tambien emite cuando la peticion
  // FALLA. En getAll() el `this.loading = false;` esta fuera del try/catch y el
  // catch solo hace console.log, asi que ante un error se emite un arreglo
  // vacio, indistinguible de "el usuario no tiene colonias". Ningun suscriptor
  // debe asumir que una emision significa exito.
  public subNeighborhoods: ReplaySubject<Neighborhood[]> = new ReplaySubject<Neighborhood[]>(1);

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { 

    this.getAll();

  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
   async create(
    neighborhood: INeighborhood
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando colonia';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(neighborhood),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 201) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll();
      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    neighborhood: Neighborhood
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhood.neighborhoodId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando colonia';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll();
      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
  
    return flag;

  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    neighborhoodId: string
  ) {

    this.loadingCurrent = true;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}`;

    const maxAttempts: number = 3;

    // Mensaje del ultimo intento fallido, para el unico toast que se muestra
    // cuando ya se agotaron todos los intentos.
    let lastMessage: string | undefined;

    let token: string | undefined;

    try {
      // El token se resuelve UNA sola vez y fuera del ciclo de reintentos:
      // si getToken() lanza es porque no hay sesion activa, y ese es un fallo
      // definitivo que no se corrige reintentando.
      token = await this.authService.getToken();
    } catch(error) {
      console.error(
        'No se pudo obtener el token de autenticacion para GET /neighborhoods/:id.',
        error
      );
    }

    if (token) {

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {

        try {

          const response = await new Promise<ServerResponse<{ neighborhood: INeighborhood }>>(
            (resolve, reject) => {
              this.http.get<IServerResponse<{ neighborhood: INeighborhood }>>(
                urlApi,
                {
                  headers: {
                    "content-type": "application/json",
                    "Authorization": token!.toString()
                  }
                }
              ).subscribe({
                next: (
                    response: IServerResponse<{ neighborhood: INeighborhood }>
                  ) => resolve(
                    new ServerResponse<{ neighborhood: INeighborhood }>(response)
                  ),
                error: (error) => {
                  if (error.error) {
                    resolve(new ServerResponse(error.error));
                  } else {
                    reject(error);
                  }
                },
              });
            }
          );

          console.log(response);

          if(response.statusCode === 200) {
            this.currentNeighborhood = new Neighborhood(response.data!.neighborhood);
            this.subCurrentNeighborhood.next(this.currentNeighborhood);

            break;
          }

          lastMessage = response.message;

          console.log(
            `Intento ${attempt} de ${maxAttempts} sin exito al obtener la colonia.`
          );

        } catch(error) {
          console.log(error);
        }

        if (attempt === maxAttempts) {

          this.toastService.show(
            {
              header: 'Error de servidor',
              body: lastMessage
                ? `${lastMessage}`
                : 'No se pudo cargar la informacion de la colonia. Vuelve a intentarlo en unos momentos.',
              classname: 'bg-danger text-white'
            }
          );

        } else {
          const delayMs: number = 1000 * attempt;

          await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        }

      }

    }

    this.loadingCurrent = false;

  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  /**
   * FUNCTION getAll
   */
  async getAll() {

    this.loading = true;

    const urlApi = `${environment.urlApi}/neighborhoods`;

    this.neighborhoods = [];

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ neighborhoods: INeighborhood[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ neighborhoods: INeighborhood[] }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{ neighborhoods: INeighborhood[] }>
              ) => resolve(
                new ServerResponse<{ neighborhoods: INeighborhood[] }>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        this.neighborhoods = response.data!.neighborhoods.map(
          neighborhood => new Neighborhood(neighborhood)
        );
      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);
      
    }

    this.loading = false;

    // Se emite aqui, fuera del try/catch, igual que `loading = false`: si la
    // peticion fallo, `this.neighborhoods` quedo en [] y de todas formas se
    // notifica, para que nadie se quede esperando una emision que nunca llega.
    // Ver la nota de subNeighborhoods: [] puede significar error o cero colonias.
    this.subNeighborhoods.next(this.neighborhoods);

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${this.currentNeighborhood!.neighborhoodId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando conjunto';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(this.currentNeighborhood!),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll();
        this.get(
          this.currentNeighborhood!.neighborhoodId
        )
      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

  async setProofRequired(
    visitTypeId: string,
    proofTypeId: string,
    status: boolean,
    processWithAi: boolean
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${this.currentNeighborhood!.neighborhoodId}/proofs-required`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando conjunto';

    const data = {
      visitTypeId,
      proofTypeId,
      status,
      processWithAi
    }

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(data),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {
        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll();
      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }
}
